/**
 * SERG card game scoring.
 * Jokers are wild (can be any card for pattern matching).
 */

function maskFromIndices(indices) {
  return indices.reduce((mask, idx) => mask | (1 << idx), 0);
}

function bitCount(mask) {
  let count = 0;
  let m = mask;
  while (m) {
    m &= m - 1;
    count++;
  }
  return count;
}

function indicesFromMask(mask, length) {
  const out = [];
  for (let i = 0; i < length; i++) {
    if (mask & (1 << i)) out.push(i);
  }
  return out;
}

function combinations(arr, k, start = 0, path = [], out = []) {
  if (path.length === k) {
    out.push([...path]);
    return out;
  }
  for (let i = start; i < arr.length; i++) {
    path.push(arr[i]);
    combinations(arr, k, i + 1, path, out);
    path.pop();
  }
  return out;
}

/** Longest run in sorted ranks (with jokers filling gaps). Returns length. */
function longestRun(sorted, jokers) {
  if (sorted.length === 0) return Math.min(jokers, 5);
  let best = 1;
  for (let i = 0; i < sorted.length; i++) {
    let run = 1;
    let j = jokers;
    let nextVal = sorted[i] + 1;
    const used = new Set([sorted[i]]);
    while (true) {
      const target = nextVal;
      const idx = sorted.findIndex((r) => r === target && !used.has(r));
      if (idx >= 0) {
        run++;
        used.add(sorted[idx]);
        nextVal++;
      } else if (j > 0) {
        run++;
        j--;
        nextVal++;
      } else break;
    }
    if (j > 0) run += j;
    best = Math.max(best, Math.min(run, 13));
  }
  return best;
}

function generateSequenceMasks(sequenceRanks, rankMap, jokerIndices) {
  const results = new Set();
  const rankOptions = sequenceRanks.map((rank) => rankMap.get(rank) || []);
  const jokerList = [...jokerIndices];

  function dfs(idx, usedMask, availableJokers) {
    if (idx === sequenceRanks.length) {
      results.add(usedMask);
      return;
    }

    for (const cardIndex of rankOptions[idx]) {
      if (usedMask & (1 << cardIndex)) continue;
      dfs(idx + 1, usedMask | (1 << cardIndex), availableJokers);
    }

    if (availableJokers.length > 0) {
      for (let j = 0; j < availableJokers.length; j++) {
        const jokerIndex = availableJokers[j];
        if (usedMask & (1 << jokerIndex)) continue;
        const nextJokers = [...availableJokers];
        nextJokers.splice(j, 1);
        dfs(idx + 1, usedMask | (1 << jokerIndex), nextJokers);
      }
    }
  }

  dfs(0, 0, jokerList);
  return results;
}

function getAceHighSequence(len) {
  const start = 15 - len;
  if (start < 1 || start > 13) return null;
  if (len < 3) return null;
  const body = Array.from({ length: len - 1 }, (_, i) => start + i);
  if (body[body.length - 1] !== 13) return null;
  return [...body, 1];
}

/**
 * Score hand of up to 10 cards.
 * @param {object} [options]
 * @param {boolean} [options.shellBonus=true] If true, +1 when every card is in a scoring group (full-hand bonus from the rules). Set false when scoring a single pile.
 * Returns { points, breakdown, unassigned, bonus, win }
 */
export function scoreHand(cards, options = {}) {
  const { shellBonus: applyShellBonus = true } = options;
  if (!cards || cards.length === 0) return { points: 0, breakdown: [], unassigned: 0, bonus: 0, win: false };
  const hand = cards;
  const rankMap = new Map();
  const suitMap = new Map([['s', []], ['h', []], ['d', []], ['c', []]]);
  const jokerIndices = [];

  hand.forEach((card, idx) => {
    if (card.isJoker) {
      jokerIndices.push(idx);
      return;
    }
    if (!rankMap.has(card.rank)) rankMap.set(card.rank, []);
    rankMap.get(card.rank).push(idx);
    suitMap.get(card.suit)?.push(idx);
  });

  const groups = [];
  const groupKeys = new Set();

  const addGroup = (mask, points, type, meta = {}) => {
    const key = `${type}:${mask}:${points}`;
    if (groupKeys.has(key)) return;
    groupKeys.add(key);
    groups.push({ mask, points, type, ...meta });
  };

  // Pairs, triples, quads
  const sizes = [
    { size: 2, basePoints: 1, type: 'pair' },
    { size: 3, basePoints: 2, type: 'triple' },
    { size: 4, basePoints: 5, type: 'quad' },
  ];
  for (const rank of Array.from({ length: 13 }, (_, i) => i + 1)) {
    const indices = rankMap.get(rank) || [];
    sizes.forEach(({ size, basePoints, type }) => {
      const maxActual = Math.min(size, indices.length);
      for (let actual = 0; actual <= maxActual; actual++) {
        const jokerNeeded = size - actual;
        if (jokerNeeded > jokerIndices.length) continue;
        const actualCombos = combinations(indices, actual);
        const jokerCombos = combinations(jokerIndices, jokerNeeded);
        actualCombos.forEach((a) => {
          jokerCombos.forEach((j) => {
            const mask = maskFromIndices([...a, ...j]);
            const points = type === 'quad' && rank === 1 ? 7 : basePoints;
            addGroup(mask, points, type, { rank });
          });
        });
      }
    });
  }

  // Full house (3 of a kind + pair) = 4 points
  const triples = groups.filter((g) => g.type === 'triple');
  const pairs = groups.filter((g) => g.type === 'pair');
  triples.forEach((t) => {
    pairs.forEach((p) => {
      if (t.mask & p.mask) return;
      if (t.rank === p.rank) return;
      addGroup(t.mask | p.mask, 4, 'full_house');
    });
  });

  // Straight (mixed suits): 5+ cards = 3 pts, 10 cards = 8 pts
  const maxLen = Math.min(10, hand.length);
  const allRanks = new Map(rankMap);
  for (let len = 5; len <= maxLen; len++) {
    for (let start = 1; start <= 14 - len; start++) {
      const seq = Array.from({ length: len }, (_, i) => start + i);
      if (seq.includes(14)) continue;
      generateSequenceMasks(seq, allRanks, jokerIndices).forEach((mask) => {
        const points = len === 10 ? 8 : 3;
        addGroup(mask, points, 'straight', { length: len });
      });
    }
    // A can be high at the end (e.g. Q-K-A, J-Q-K-A, 10-J-Q-K-A, 9-10-J-Q-K-A).
    const aceHighSeq = getAceHighSequence(len);
    if (aceHighSeq) {
      generateSequenceMasks(aceHighSeq, allRanks, jokerIndices).forEach((mask) => {
        addGroup(mask, 3, 'straight', { length: len, royal: len === 5 && aceHighSeq[0] === 10 });
      });
    }
  }

  // Flush (same suit, non-sequential): 5+ cards = 3 pts, 10 cards = 8 pts
  for (const suit of ['s', 'h', 'd', 'c']) {
    const suitIndices = suitMap.get(suit) || [];
    const pool = [...suitIndices, ...jokerIndices];
    const maxSize = Math.min(10, pool.length);
    for (let size = 5; size <= maxSize; size++) {
      combinations(pool, size).forEach((combo) => {
        const nonJokerRanks = combo
          .map((idx) => hand[idx])
          .filter((c) => !c.isJoker)
          .map((c) => c.rank);
        const jokerCount = combo.length - nonJokerRanks.length;
        const runLen = longestRun([...new Set(nonJokerRanks)].sort((a, b) => a - b), jokerCount);
        if (runLen >= 5) return;
        const points = size === 10 ? 8 : 3;
        addGroup(maskFromIndices(combo), points, 'flush', { length: size });
      });
    }
  }

  // Straight flush (same suit): 3+ cards, 3 pts base + 1 per extra card
  for (const suit of ['s', 'h', 'd', 'c']) {
    const suitRanks = new Map();
    const suitIndices = suitMap.get(suit) || [];
    suitIndices.forEach((idx) => {
      const r = hand[idx].rank;
      if (!suitRanks.has(r)) suitRanks.set(r, []);
      suitRanks.get(r).push(idx);
    });

    for (let len = 3; len <= maxLen; len++) {
      for (let start = 1; start <= 14 - len; start++) {
        const seq = Array.from({ length: len }, (_, i) => start + i);
        if (seq.includes(14)) continue;
        generateSequenceMasks(seq, suitRanks, jokerIndices).forEach((mask) => {
          if (len === 10) {
            addGroup(mask, 0, 'straight_flush', { length: len, win: true });
            return;
          }
          const points = 3 + (len - 3);
          addGroup(mask, points, 'straight_flush', { length: len });
        });
      }
      const aceHighSeq = getAceHighSequence(len);
      if (aceHighSeq) {
        generateSequenceMasks(aceHighSeq, suitRanks, jokerIndices).forEach((mask) => {
          if (len === 5 && aceHighSeq[0] === 10) {
            addGroup(mask, 10, 'straight_flush', { length: 5, royal: true });
            return;
          }
          const points = 3 + (len - 3);
          addGroup(mask, points, 'straight_flush', { length: len });
        });
      }
    }
  }

  // DP over masks to maximize points
  const n = hand.length;
  const maxMask = 1 << n;
  const best = Array(maxMask).fill(-Infinity);
  const prevMask = Array(maxMask).fill(-1);
  const prevGroup = Array(maxMask).fill(-1);
  best[0] = 0;

  groups.forEach((group, groupIdx) => {
    for (let mask = maxMask - 1; mask >= 0; mask--) {
      if (best[mask] === -Infinity) continue;
      if (mask & group.mask) continue;
      const next = mask | group.mask;
      const score = best[mask] + group.points;
      if (score > best[next]) {
        best[next] = score;
        prevMask[next] = mask;
        prevGroup[next] = groupIdx;
      }
    }
  });

  let bestPoints = -Infinity;
  let bestMask = 0;
  for (let mask = 0; mask < maxMask; mask++) {
    if (best[mask] === -Infinity) continue;
    const usedCount = bitCount(mask);
    const unassigned = n - usedCount;
    const bonus = applyShellBonus && unassigned === 0 ? 1 : 0;
    const total = best[mask] - unassigned + bonus;
    if (total > bestPoints) {
      bestPoints = total;
      bestMask = mask;
    }
  }

  const usedCount = bitCount(bestMask);
  const unassigned = n - usedCount;
  const bonus = applyShellBonus && unassigned === 0 ? 1 : 0;
  const selectedGroups = [];
  let traceMask = bestMask;
  while (traceMask > 0 && prevMask[traceMask] >= 0 && prevGroup[traceMask] >= 0) {
    const g = groups[prevGroup[traceMask]];
    selectedGroups.push({
      points: g.points,
      type: g.type,
      length: g.length ?? bitCount(g.mask),
      royal: Boolean(g.royal),
      rank: g.rank ?? null,
      cards: indicesFromMask(g.mask, hand.length).map((idx) => hand[idx]),
    });
    traceMask = prevMask[traceMask];
  }
  selectedGroups.reverse();

  const win = selectedGroups.some((g) => g.type === 'straight_flush' && g.length === 10);
  return { points: bestPoints, breakdown: selectedGroups, unassigned, bonus, win };
}

/**
 * Score a single pile.
 * Cards that contribute to scoring groups add points.
 * Cards in the pile that do not contribute are treated as dead cards (-1 each).
 */
export function scorePile(cards) {
  if (!cards || cards.length === 0) return { points: 0, type: 'invalid', unassigned: 0 };
  const result = scoreHand(cards, { shellBonus: false });
  const first = result.breakdown?.[0];
  return {
    points: result.points,
    type: first?.type || 'invalid',
    cards: cards,
    unassigned: result.unassigned ?? 0,
  };
}

/**
 * Score a player's round from their piles + remaining hand.
 * Pile cards are scored by best groups inside each pile, with non-contributing pile cards = -1 each.
 * Each leftover hand card also = -1.
 * Optional +1 if all 10 cards are in piles and every pile fully contributes (no unassigned pile cards).
 *
 * @param {object} [options]
 * @param {number} [options.unpickedDiscardPenalty=0] Extra −1 per obligation: current player must pick discard but SURGE was called with a card still on the pile.
 * Returns { total, pileScores, penalty, roundShellBonus }
 */
export function scorePlayerRound(piles, remainingHand, options = {}) {
  const { unpickedDiscardPenalty = 0 } = options;
  const pileScores = (piles || []).map((pile) => scorePile(pile));
  const pileTotal = pileScores.reduce((sum, p) => sum + p.points, 0);
  const handLen = (remainingHand || []).length;
  const penalty = handLen + unpickedDiscardPenalty;
  const totalInPiles = (piles || []).reduce((sum, p) => sum + p.length, 0);
  const roundShellBonus =
    handLen === 0 &&
    totalInPiles === 10 &&
    (piles || []).length > 0 &&
    pileScores.every((ps) => ps.points > 0 && (ps.unassigned ?? 0) === 0)
      ? 1
      : 0;
  const allCards = [...(remainingHand || []), ...((piles || []).flat())];
  const bestPossible = scoreHand(allCards);
  const maxPotential = bestPossible.points;
  const total = pileTotal - penalty + roundShellBonus;
  return {
    total,
    pileScores,
    penalty,
    roundShellBonus,
    maxPotential,
    missedPotential: Math.max(0, maxPotential - total),
    instantWin: Boolean(bestPossible.win),
  };
}

export const POINTS_TO_WIN = 40;
